// External
import React, { useEffect, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import * as _ from 'lodash';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  DragOverlay,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

// Internal
import Dropzone from '../../../../components/Dropzone';
import {
  ListingDetails_ListingData_listing,
  ListingDetails_ListingData_listing_property_media,
} from '../../../../types/graph/ListingDetails_ListingData';
import SortableImage from './Images/SortableImage';
import Image from './Images/Image';

interface IProps {
  listing: ListingDetails_ListingData_listing;
}

const Images: React.FC<IProps> = ({ listing }) => {
  // Hooks
  const [items, setItems] = useState<
    ListingDetails_ListingData_listing_property_media[]
  >(_.get(listing, 'property.media', []));

  const [saving, setSaving] = useState<boolean>(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  // Storage
  const [updateListing, { loading, error, data }] = useMutation(
    LISTING_UPDATE,
    {
      onCompleted: (data) => {
        setSaving(false);
      },
    }
  );

  // Update items on listing change
  useEffect(() => {
    setItems(_.get(listing, 'property.media', []));
  }, [listing]);

  // Handlers
  const onDrop = (files: File[]) => {
    console.log('media files: ', files);
    setSaving(true);
    updateListing({
      variables: {
        listingId: listing.id,
        listing: { property: { mediaUpdate: { upload: files } } },
      },
    });
  };

  const onDeleteImage = (delSid: string) => {
    updateListing({
      variables: {
        listingId: listing.id,
        listing: {
          property: {
            mediaUpdate: {
              remove: delSid,
            },
          },
        },
      },
    });
  };

  const onReorder = (
    files: ListingDetails_ListingData_listing_property_media[]
  ) => {
    //console.log('media file reorder: ', files);
    updateListing({
      variables: {
        listingId: listing.id,
        listing: {
          property: {
            mediaUpdate: {
              order: files.map((i) => {
                return { sid: i.sid };
              }),
            },
          },
        },
      },
    });
  };

  function handleDragStart(event: DragStartEvent) {
    //console.log('drag start: activeId: ', event.active.id);
    setActiveId(event.active.id);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    let orderedItems: ListingDetails_ListingData_listing_property_media[] = [];

    if (active.id !== over?.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((i) => i.sid === active.id);
        const newIndex = items.findIndex((i) => i.sid === over?.id);

        orderedItems = arrayMove(items, oldIndex, newIndex);

        return orderedItems;
      });
    }

    onReorder(orderedItems);

    setActiveId(null);
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  // User feedback on load/save
  if (saving) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div>
        <Dropzone
          label="Upload images"
          comment="JPG, PNG or compatible image types"
          onDrop={onDrop}
        />
      </div>
      {/* <div className="mt-8 grid grid-cols-5 gap-4">
        {listing.property?.media &&
          listing.property.media.map((media) => (
            <img
              className="col-span-1 w-full h-full object-cover rounded-lg shadow"
              src={media!.filePath!}
              alt="Property"
            />
          ))}
      </div> */}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={items.filter((i) => i.type === 'image').map((i) => i.sid!)}
          strategy={rectSortingStrategy}
        >
          <div className="mt-8 grid grid-cols-5 gap-4">
            {items
              .filter((i) => i.type === 'image')
              .map((item, index) => (
                <SortableImage
                  key={item.sid!}
                  id={item.sid!}
                  item={item}
                  index={index}
                  active={activeId}
                  onDeleteImage={onDeleteImage}
                />
              ))}
          </div>
        </SortableContext>

        <DragOverlay adjustScale={true}>
          {activeId ? (
            <Image
              id={items[items.findIndex((i) => i.sid === activeId)].sid}
              key={items[items.findIndex((i) => i.sid === activeId)].sid}
              item={items[items.findIndex((i) => i.sid === activeId)]}
              index={items.findIndex((i) => i.sid === activeId)}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default Images;

const LISTING_UPDATE = gql`
  mutation Listing_ImagesUpdateData(
    $listingId: String!
    $listing: ListingInput!
  ) {
    listingUpdate(listingId: $listingId, listing: $listing) {
      id
      sid
      property {
        address {
          street
          city
          region
          postal
          country
          lat
          lng
        }
        media {
          sid
          filePath
        }
        prices {
          amount
          setAt
        }
        bedrooms
        bathrooms
        dimensions
        year
        description
      }
      website {
        styling {
          template
          colors {
            primary
            accent
          }
        }
      }
      contacts {
        id
        person {
          name {
            first
            last
            full
          }
          email
          phones {
            number
            type
          }
          socialLinks {
            url
            type
          }
          avatar {
            filePath
          }
        }
        company {
          name
          title
          socialLinks {
            url
            type
          }
          logo {
            filePath
          }
        }
      }
    }
  }
`;
